import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import type { ApiError, ApiResponse } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const DeleteDraftSchema = z.object({
  draftId: z.string(),
  threadId: z.string(),
});

export type DeleteDraftVariables = z.infer<typeof DeleteDraftSchema>;

async function deleteDraftFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteDraftVariables,
): Promise<ApiResponse> {
  const instance = await axiosInstance;
  return instance.delete(`mails/drafts/${variables.draftId}`);
}

export function useDeleteDraft(
  options: MutationOptions<ApiResponse, ApiError, DeleteDraftVariables> = {},
) {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteDraftVariables>({
    mutationFn: variables => deleteDraftFn(axiosInstance, variables),
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: ['mail', variables.threadId] });
      client.invalidateQueries({ queryKey: ['mails', 'inbox'], refetchType: 'all' });
      client.invalidateQueries({ queryKey: ['mails', 'drafts'], refetchType: 'all' });
    },
    ...options,
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, DeleteDraftVariables, 'deleteDraft'>(
    'deleteDraft',
    mutation,
  );
}
